import type { NavEdge, NavGraph, NavNode } from '../types';
import { pgSelect, pgInsert, pgUpdate, pgDeleteWhere, isSupabaseConfigured } from './supabaseClient';
import { loadConfig } from './configService';

/**
 * Navigation graph access layer — Supabase-backed (`nav_nodes`/`nav_edges`
 * tables). Supabase is the single source of truth: if it's unconfigured or
 * unreachable the graph is empty (navmesh/direct routing still function).
 * Was previously routeService.ts's synchronous `getGraph()`; moved here and
 * made async now that it's a network call, and split out since routeService
 * should stay pure/independent of any data source (per project rules).
 *
 * Scoped by map_code (2026-07-21, same fix as poiService.ts/tourService.ts —
 * without it two maps' routing graphs stayed tangled together even once
 * POIs themselves were correctly scoped).
 */

interface NodeRow {
  id: string;
  floor: number;
  x: number;
  y: number;
  z: number;
  kind: string | null;
}

interface EdgeRow {
  id: number;
  from_node: string;
  to_node: string;
  weight: number | null;
  accessible: boolean;
  kind: string | null;
  bidirectional: boolean;
}

function rowToNode(r: NodeRow): NavNode {
  return { id: r.id, floor: r.floor, position: { x: r.x, y: r.y, z: r.z }, kind: r.kind ?? undefined };
}

function rowToEdge(r: EdgeRow): NavEdge {
  return {
    from: r.from_node,
    to: r.to_node,
    weight: r.weight ?? undefined,
    accessible: r.accessible,
    kind: r.kind ?? undefined,
    bidirectional: r.bidirectional,
  };
}

let cache: NavGraph | null = null;
let cacheMapCode: string | null = null;

const EMPTY_GRAPH: NavGraph = { nodes: [], edges: [] };
function offlineKey(mapCode: string): string {
  return `navme.cache.graph.${mapCode || 'default'}`;
}

export async function getGraph(forceRefresh = false): Promise<NavGraph> {
  const mapCode = loadConfig().code;
  if (cache && !forceRefresh && cacheMapCode === mapCode) return cache;
  if (!isSupabaseConfigured()) {
    console.warn('getGraph: database not configured — empty nav graph (navmesh/direct routing still work)');
    return EMPTY_GRAPH;
  }
  try {
    const mapFilter = `map_code=eq.${encodeURIComponent(mapCode)}`;
    const [nodeRows, edgeRows] = await Promise.all([
      pgSelect<NodeRow>('nav_nodes', `select=*&${mapFilter}`),
      pgSelect<EdgeRow>('nav_edges', `select=*&${mapFilter}`),
    ]);
    cache = { nodes: nodeRows.map(rowToNode), edges: edgeRows.map(rowToEdge) };
    cacheMapCode = mapCode;
    try {
      localStorage.setItem(offlineKey(mapCode), JSON.stringify(cache));
    } catch {
      /* quota */
    }
    return cache;
  } catch (err) {
    console.error('getGraph: fetch failed — trying offline snapshot', err);
    try {
      const snap = JSON.parse(localStorage.getItem(offlineKey(mapCode)) ?? 'null') as NavGraph | null;
      if (snap && snap.nodes.length > 0) return snap;
    } catch {
      /* corrupt snapshot */
    }
    return EMPTY_GRAPH; // not cached — retried on next call
  }
}

export function invalidateGraphCache(): void {
  cache = null;
  cacheMapCode = null;
}

export async function addNode(node: NavNode): Promise<void> {
  await pgInsert('nav_nodes', {
    id: node.id,
    floor: node.floor,
    x: node.position.x,
    y: node.position.y,
    z: node.position.z,
    kind: node.kind ?? null,
    map_code: loadConfig().code,
  });
  invalidateGraphCache();
}

export async function addEdge(edge: NavEdge): Promise<void> {
  await pgInsert('nav_edges', {
    from_node: edge.from,
    to_node: edge.to,
    weight: edge.weight ?? null,
    accessible: edge.accessible ?? true,
    kind: edge.kind ?? null,
    bidirectional: edge.bidirectional ?? true,
    map_code: loadConfig().code,
  });
  invalidateGraphCache();
}

/** Moves a nav node (used when a POI is repositioned so routing follows). */
export async function updateNodePosition(
  nodeId: string,
  pos: { x: number; y: number; z: number },
  floor?: number,
): Promise<void> {
  const patch: Record<string, unknown> = { x: pos.x, y: pos.y, z: pos.z };
  if (floor !== undefined) patch.floor = floor;
  await pgUpdate('nav_nodes', nodeId, patch);
  invalidateGraphCache();
}

/** Deletes the edge(s) between two nodes, either direction. */
export async function deleteEdgeBetween(a: string, b: string): Promise<void> {
  const ea = encodeURIComponent(a);
  const eb = encodeURIComponent(b);
  await pgDeleteWhere(
    'nav_edges',
    `or=(and(from_node.eq.${ea},to_node.eq.${eb}),and(from_node.eq.${eb},to_node.eq.${ea}))`,
  );
  invalidateGraphCache();
}

/**
 * Deletes a nav node and every edge touching it. Used when deleting a POI so
 * its auto-created node/edges don't linger as orphans in the graph.
 */
export async function deleteNodeWithEdges(nodeId: string): Promise<void> {
  const encoded = encodeURIComponent(nodeId);
  await pgDeleteWhere('nav_edges', `or=(from_node.eq.${encoded},to_node.eq.${encoded})`);
  await pgDeleteWhere('nav_nodes', `id=eq.${encoded}`);
  invalidateGraphCache();
}
